<script setup>
import {computed, onMounted, ref, watch} from 'vue'

const props = defineProps({
    displayTitle: {
        type: Boolean,
        required: false,
        default: true
    },
    leadId: {
        type: [Number, String],
        required: true,
    },
})

const emailDeliveries = ref([])
const smsDeliveries = ref([])
const loading = ref(false)
const loaded = ref(false)
const emailDeliveriesDisplayLimit = ref(5)
const smsDeliveriesDisplayLimit = ref(5)

const applyDeliveries = (payload = {}) => {
    emailDeliveries.value = payload.email_deliveries ?? []
    smsDeliveries.value = payload.sms_deliveries ?? []
}

const resetDisplayLimits = () => {
    emailDeliveriesDisplayLimit.value = 5
    smsDeliveriesDisplayLimit.value = 5
}

const loadDeliveries = async () => {
    if (!props.leadId) {
        applyDeliveries()
        loaded.value = true
        return
    }

    loaded.value = false
    loading.value = true

    try {
        const response = await axios.get(route('bo.api.v1.crm.lead.automations', props.leadId))

        applyDeliveries(response.data)
    } finally {
        loading.value = false
        loaded.value = true
    }
}

const visibleEmailDeliveries = computed(() => {
    if (emailDeliveriesDisplayLimit.value === null) {
        return emailDeliveries.value
    }

    return emailDeliveries.value.slice(0, emailDeliveriesDisplayLimit.value)
})

const visibleSmsDeliveries = computed(() => {
    if (smsDeliveriesDisplayLimit.value === null) {
        return smsDeliveries.value
    }

    return smsDeliveries.value.slice(0, smsDeliveriesDisplayLimit.value)
})

watch(
    () => props.leadId,
    (nextLeadId, previousLeadId) => {
        if (nextLeadId && nextLeadId !== previousLeadId) {
            resetDisplayLimits()
            loadDeliveries()
            return
        }

        if (!nextLeadId) {
            applyDeliveries()
            loaded.value = true
        }
    }
)

onMounted(() => {
    loadDeliveries()
})
</script>

<template>
    <h2 v-if="displayTitle">{{ _mft('crm:delivery.domainNamePlural') }}</h2>
    <div
        v-if="loading && !loaded"
        class="flex justify-center"
    >
        <mf-spinner-medium class="w-12 h-6" />
    </div>

    <div
        v-else
        class="lg:grid gap-4"
        :class="visibleSmsDeliveries > 0 ? 'lg:grid-cols-2' : 'lg:grid-cols-1'"
    >
        <list-basic class="mb-4 lg:mb-0">
            <template #header>
                <h3 class="text-gray-600">{{ _mft('crm:delivery.email.title') }}</h3>
            </template>
            <list-basic-item
                v-for="delivery in visibleEmailDeliveries"
                :key="delivery.id"
            >
                <b>{{ _mft('crm:delivery.source.label') }}: </b>
                <a
                    v-if="delivery.automation_id"
                    :href="route('crm.communications.automations.show', delivery.automation_id)"
                    target="_blank"
                    :title="_mft('crm:delivery.toToAutomation')"
                    class="mf-link"
                >
                    {{ delivery.automation_name }}
                </a>
                <a
                    v-else
                    :href="route('crm.communications.email.deliveries.previewBrowser', delivery.uuid)"
                    target="_blank"
                    :title="_mft('crm:delivery.toToAutomation')"
                    class="mf-link"
                >
                    {{ _mft('crm:delivery.source.workflow') }} <i class="fa-solid fa-up-right-from-square ml-1 opacity-50 text-xxs -translate-y-0.5"></i>
                </a>
                <br>
                <b>{{ _mft('crm:delivery.sentAt') }}:</b> {{ delivery.sent_at }}<br>
                <b>{{ _mft('crm:delivery.title') }}:</b> {{ delivery.subject }}<br>
                <b>{{ _mft('crm:automationFilter.status.opened') }}:</b> {{ delivery.opened ? _mft('shared:hasProperty.positive') : _mft('shared:hasProperty.negative') }}<br>
            </list-basic-item>

            <list-basic-item v-if="emailDeliveries.length === 0">
                <i>{{ _mft('crm:delivery.noEmailsSent') }}.</i>
            </list-basic-item>

            <template
                v-if="emailDeliveries.length > emailDeliveriesDisplayLimit && emailDeliveriesDisplayLimit !== null"
                #footer
            >
                <div class="flex justify-center pt-2">
                    <button-secondary
                        @click="emailDeliveriesDisplayLimit = null"
                    >
                        {{ _mft('shared:expand.showAll') }}
                    </button-secondary>
                </div>
            </template>
        </list-basic>

        <list-basic v-if="visibleSmsDeliveries > 0">
            <template #header>
                <h3 class="text-gray-600">{{ _mft('crm:delivery.sms.title') }}</h3>
            </template>

            <list-basic-item
                v-for="delivery in visibleSmsDeliveries"
                :key="delivery.id"
            >
                <b>{{ _mft('crm:delivery.source.label') }}: </b>
                <a
                    v-if="delivery.automation_id"
                    :href="route('crm.communications.automations.show', delivery.automation_id)"
                    target="_blank"
                    :title="_mft('crm:delivery.toToAutomation')"
                    class="mf-link"
                >
                    {{ delivery.automation_name }}
                </a>
                <!-- No SMS equivalent of the browser preview, so this stays unlinked. -->
                <span v-else>{{ _mft('crm:delivery.source.workflow') }}</span>
                <br>
                <b>{{ _mft('crm:delivery.sentAt') }}:</b> {{ delivery.sent_at }}<br>
            </list-basic-item>

            <list-basic-item v-if="smsDeliveries.length === 0">
                <i>{{ _mft('crm:delivery.noSmsSent') }}.</i>
            </list-basic-item>

            <template
                v-if="smsDeliveries.length > smsDeliveriesDisplayLimit && smsDeliveriesDisplayLimit !== null"
                #footer
            >
                <div class="flex justify-center pt-2">
                    <button-secondary @click="smsDeliveriesDisplayLimit = null">
                        {{ _mft('shared:expand.showAll') }}
                    </button-secondary>
                </div>
            </template>
        </list-basic>
    </div>
</template>
